import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../../api/client';

const ENTITY_CONFIG = {
  authors: {
    queueEndpoint: '/admin/master/linking-queue/authors',
    confirmEndpoint: '/admin/master/linking/authors/confirm',
    skipEndpoint: '/admin/master/linking/authors/skip',
    freeTextField: 'free_text_author',
    idField: 'author_id',
    nameField: 'name',
    nameSecondaryField: 'name_sinhala',
    hasRawField: false,
    label: 'Author',
  },
  publishers: {
    queueEndpoint: '/admin/master/linking-queue/publishers',
    confirmEndpoint: '/admin/master/linking/publishers/confirm',
    skipEndpoint: '/admin/master/linking/publishers/skip',
    freeTextField: 'free_text_publisher',
    idField: 'publisher_id',
    nameField: 'name_en',
    nameSecondaryField: 'name_si',
    hasRawField: false,
    label: 'Publisher',
  },
  genres: {
    queueEndpoint: '/admin/master/linking-queue/genres',
    confirmEndpoint: '/admin/master/linking/genres/confirm',
    skipEndpoint: '/admin/master/linking/genres/skip',
    freeTextField: 'free_text_genre',
    idField: 'genre_id',
    nameField: 'name_en',
    nameSecondaryField: 'name_si',
    hasRawField: true,
    label: 'Genre',
  },
  translators: {
    queueEndpoint: '/admin/master/linking-queue/translators',
    confirmEndpoint: '/admin/master/linking/translators/confirm',
    skipEndpoint: '/admin/master/linking/translators/skip',
    freeTextField: 'free_text_translator',
    idField: 'translator_id',
    nameField: 'name',
    nameSecondaryField: 'name_sinhala',
    hasRawField: false,
    label: 'Translator',
  },
  original_authors: {
    queueEndpoint: '/admin/master/linking-queue/original-authors',
    confirmEndpoint: '/admin/master/linking/original-authors/confirm',
    skipEndpoint: '/admin/master/linking/original-authors/skip',
    freeTextField: 'free_text_original_author',
    idField: 'author_id',
    nameField: 'name',
    nameSecondaryField: 'name_sinhala',
    hasRawField: false,
    label: 'Original Author',
  },
};

interface LinkingAssistantProps {
  entityType: 'authors' | 'publishers' | 'genres' | 'translators' | 'original_authors';
}

export default function LinkingAssistant({ entityType }: LinkingAssistantProps) {
  const config = ENTITY_CONFIG[entityType];

  const [sourceTable, setSourceTable] = useState<'books' | 'sinhala_books'>('books');
  const [queueStatus, setQueueStatus] = useState<'pending' | 'skipped'>('pending');
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const [items, setItems] = useState<any[]>([]);
  const [totalUnlinked, setTotalUnlinked] = useState(0);
  const [totalSkipped, setTotalSkipped] = useState(0);
  const [totalReviewed, setTotalReviewed] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [loadedScope, setLoadedScope] = useState<string | null>(null);

  const [selections, setSelections] = useState<Record<string, string>>({});
  const [busyRecords, setBusyRecords] = useState<Record<string, boolean>>({});
  const [awaitingSync, setAwaitingSync] = useState<Record<string, boolean>>({});
  const inFlight = useRef(new Set<string>());
  const uncertainWrites = useRef(new Set<string>());
  const completed = useRef(new Set<string>());
  const rows = useRef<any[]>([]);
  const selectedIds = useRef<Record<string, string>>({});
  const snapshotScope = useRef<string | null>(null);
  const savedSinceSync = useRef(false);
  const mounted = useRef(false);
  const queueRequest = useRef(0);
  const actionRequest = useRef(0);
  const scope = JSON.stringify([entityType, sourceTable, offset, queueStatus]);
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const queueReady = loadedScope === scope && !loading;
  const anySaving = Object.keys(busyRecords).length > 0;
  const countsReady = queueReady && !syncing && !error && !anySaving;
  const isSkipped = queueStatus === 'skipped';
  const totalVisible = isSkipped ? totalSkipped : totalUnlinked;
  const invalidateRequests = useCallback(() => { queueRequest.current++; actionRequest.current++; }, []);
  const sameReviewContext = useCallback((before: any, after: any) => !!before && !!after &&
    before.record_id === after.record_id && before.source_table === after.source_table &&
    before[config.freeTextField] === after[config.freeTextField] &&
    (!config.hasRawField || before.raw_genre_field === after.raw_genre_field),
  [config.freeTextField, config.hasRawField]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; invalidateRequests(); };
  }, [invalidateRequests]);

  const fetchQueue = useCallback(async (background = false) => {
    if (!mounted.current || activeScope.current !== scope || inFlight.current.size > 0) return;
    const preserveRows = background && snapshotScope.current === scope;
    const request = ++queueRequest.current;
    const isCurrent = () => mounted.current && request === queueRequest.current && activeScope.current === scope;
    if (preserveRows) {
      setSyncing(true);
    } else {
      setLoading(true); setSyncing(false);
      snapshotScope.current = null;
      rows.current = []; selectedIds.current = {};
      setLoadedScope(null); setItems([]); setSelections({});
    }
    setError(null);
    try {
      const res = await adminApi.get(config.queueEndpoint, {
        params: { source_table: sourceTable, limit, offset, ...(isSkipped ? { queue_status: 'skipped' } : {}) }
      });
      if (!isCurrent()) return;
      const data = res.data;
      const visibleCount = isSkipped ? data?.total_skipped : data?.total_unlinked_remaining;
      if (!data || !Array.isArray(data.items) || data.items.length > limit || !data.items.every((item: any) =>
        item && item.source_table === sourceTable && typeof item.record_id === 'string' && item.record_id.trim() &&
        typeof item[config.freeTextField] === 'string' && Array.isArray(item.suggestions) &&
        item.suggestions.every((suggestion: any) => suggestion && typeof suggestion[config.idField] === 'string' &&
          suggestion[config.idField].trim() && Number.isFinite(suggestion.match_score))) ||
        new Set(data.items.map((item: any) => item.record_id)).size !== data.items.length ||
        !['total_unlinked_remaining', 'total_reviewed', 'total_records'].every(key =>
          Number.isSafeInteger(data[key]) && data[key] >= 0) ||
        !Number.isSafeInteger(visibleCount) || visibleCount < 0 || data.items.length > visibleCount ||
        (isSkipped ? data.queue_status !== 'skipped' : data.queue_status && data.queue_status !== 'pending') ||
        !Number.isFinite(data.progress_percent) || data.progress_percent < 0 || data.progress_percent > 100) {
        throw new Error('Incomplete linking queue');
      }
      if (data.items.some((item: any) => completed.current.has(item.record_id))) {
        throw new Error('Completed record reappeared in queue');
      }
      // Reviews remove rows from this queue. Recover if the page no longer exists.
      if (data.items.length === 0 && offset > 0) {
        const lastOffset = Math.max(0, Math.floor((visibleCount - 1) / limit) * limit);
        if (lastOffset < offset) { snapshotScope.current = null; invalidateRequests(); setOffset(lastOffset); return; }
      }
      const previousRows = new Map(rows.current.map(item => [item.record_id, item]));
      const nextSelections: Record<string, string> = {};
      const changedSelections = new Set<string>();
      const nextItems = data.items.map((item: any) => {
        const selected = selectedIds.current[item.record_id];
        const previous = previousRows.get(item.record_id);
        if (selected && sameReviewContext(previous, item)) {
          nextSelections[item.record_id] = selected;
          // Keep the user's explicit choice visible if it left the fuzzy shortlist.
          const chosen = previous.suggestions.find((suggestion: any) => suggestion[config.idField] === selected);
          if (chosen && !item.suggestions.some((suggestion: any) => suggestion[config.idField] === selected)) {
            return { ...item, suggestions: [...item.suggestions, chosen] };
          }
        } else if (selected) changedSelections.add(item.record_id);
        return item;
      });
      rows.current = nextItems; selectedIds.current = nextSelections;
      snapshotScope.current = scope;
      completed.current.clear(); uncertainWrites.current.clear(); savedSinceSync.current = false;
      setAwaitingSync({});
      setItems(nextItems); setSelections(nextSelections);
      setActionErrors(previous => Object.fromEntries(nextItems.flatMap((item: any) => {
        if (changedSelections.has(item.record_id)) return [[item.record_id, 'Record changed; review the new value before linking.']];
        return previous[item.record_id] && sameReviewContext(previousRows.get(item.record_id), item)
          ? [[item.record_id, previous[item.record_id]]] : [];
      })));
      setTotalUnlinked(data.total_unlinked_remaining);
      setTotalSkipped(isSkipped ? data.total_skipped : 0);
      setTotalReviewed(data.total_reviewed);
      setTotalRecords(data.total_records);
      setProgressPercent(data.progress_percent);
      setLoadedScope(scope);
    } catch (err: any) {
      if (!isCurrent()) return;
      setError(savedSinceSync.current ? 'Action saved, but linking queue could not be refreshed. Retry queue.'
        : 'Linking queue unavailable. Retry to load current counts.');
      setLoadedScope(scope);
    } finally {
      if (isCurrent()) { setLoading(false); setSyncing(false); }
    }
  }, [config.queueEndpoint, config.freeTextField, config.idField, sourceTable, offset, scope, isSkipped, sameReviewContext, invalidateRequests]);

  useEffect(() => {
    actionRequest.current++;
    inFlight.current.clear(); completed.current.clear(); uncertainWrites.current.clear(); savedSinceSync.current = false;
    setBusyRecords({}); setAwaitingSync({}); setActionErrors({});
    fetchQueue();
    return invalidateRequests;
  }, [fetchQueue, invalidateRequests]);

  const handleSourceTableChange = (st: 'books' | 'sinhala_books') => {
    if (st !== sourceTable && inFlight.current.size === 0) {
      snapshotScope.current = null;
      invalidateRequests();
      setSourceTable(st);
      setOffset(0);
    }
  };

  const changePage = (nextOffset: number) => {
    if (!countsReady || snapshotScope.current !== scope || inFlight.current.size > 0 || completed.current.size > 0) return;
    snapshotScope.current = null;
    invalidateRequests();
    setOffset(nextOffset);
  };

  const changeQueueStatus = (status: 'pending' | 'skipped') => {
    if (status === queueStatus || inFlight.current.size > 0) return;
    snapshotScope.current = null;
    invalidateRequests();
    setQueueStatus(status);
    setOffset(0);
  };

  const selectSuggestion = (item: any, id: string) => {
    if (!queueReady || snapshotScope.current !== scope || inFlight.current.has(item.record_id) ||
        !sameReviewContext(item, rows.current.find(row => row.record_id === item.record_id))) return;
    selectedIds.current = { ...selectedIds.current, [item.record_id]: id };
    setSelections(selectedIds.current);
    setActionErrors(previous => { const next = { ...previous }; delete next[item.record_id]; return next; });
  };

  const handleAction = async (item: any, action: 'confirm' | 'skip') => {
    const recordId = item.record_id;
    const selected = selectedIds.current[recordId];
    if (!queueReady || snapshotScope.current !== scope || activeScope.current !== scope ||
        inFlight.current.has(recordId) || completed.current.has(recordId) || uncertainWrites.current.has(recordId) ||
        !sameReviewContext(item, rows.current.find(row => row.record_id === recordId)) ||
        (isSkipped && action === 'skip') ||
        (action === 'confirm' && !selected)) return;
    inFlight.current.add(recordId);
    // Any read begun before this write cannot certify the resulting queue/counts.
    queueRequest.current++;
    const request = actionRequest.current;
    const isCurrent = () => mounted.current && request === actionRequest.current && activeScope.current === scope;
    setBusyRecords(previous => ({ ...previous, [recordId]: true }));
    setSyncing(true); setError(null);
    setActionErrors(previous => { const next = { ...previous }; delete next[recordId]; return next; });

    try {
      let endpoint = '';
      let payload: any = {
        source_table: sourceTable,
        record_id: recordId,
      };

      if (action === 'confirm') {
        endpoint = config.confirmEndpoint;
        payload[config.idField] = selected;
      } else {
        endpoint = config.skipEndpoint;
      }

      const res = await adminApi.post(endpoint, payload);
      if (!isCurrent()) return;
      if (res.data?.success !== true) throw new Error('Unsuccessful linking action');
      completed.current.add(recordId); savedSinceSync.current = true;
      rows.current = rows.current.filter(row => row.record_id !== recordId);
      const nextSelections = { ...selectedIds.current }; delete nextSelections[recordId];
      selectedIds.current = nextSelections;
      setItems(rows.current); setSelections(nextSelections);
    } catch (err: any) {
      if (!isCurrent()) return;
      uncertainWrites.current.add(recordId);
      setAwaitingSync(previous => ({ ...previous, [recordId]: true }));
      setActionErrors(previous => ({ ...previous, [recordId]: typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail : `Failed to ${action} link` }));
    } finally {
      if (isCurrent()) {
        inFlight.current.delete(recordId);
        setBusyRecords(previous => { const next = { ...previous }; delete next[recordId]; return next; });
        // Reconcile overlapping writes once, without clearing or blocking other rows.
        if (inFlight.current.size === 0) void fetchQueue(true);
      }
    }
  };

  const sourceLabel = sourceTable === 'books' ? 'Books' : 'Sinhala Books';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Top Bar */}
      <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={sourceTable === 'books'}
            disabled={anySaving}
            onClick={() => handleSourceTableChange('books')}
            className={`px-4 py-2 rounded font-medium text-sm transition-colors ${
              sourceTable === 'books' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            📗 Books
          </button>
          <button
            type="button"
            aria-pressed={sourceTable === 'sinhala_books'}
            disabled={anySaving}
            onClick={() => handleSourceTableChange('sinhala_books')}
            className={`px-4 py-2 rounded font-medium text-sm transition-colors ${
              sourceTable === 'sinhala_books' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            📕 Sinhala Books
          </button>
          <button type="button" aria-pressed={isSkipped} disabled={anySaving}
            onClick={() => changeQueueStatus('skipped')}
            className={`px-4 py-2 rounded font-medium text-sm transition-colors ${isSkipped
              ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}>
            Skipped
          </button>
          {isSkipped && <button type="button" disabled={anySaving} onClick={() => changeQueueStatus('pending')}
            className="px-4 py-2 rounded text-sm border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">
            Back to pending
          </button>}
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="flex flex-col items-end">
            <div className="text-sm font-semibold text-[#085041] mb-1" aria-live="polite">
              {countsReady ? isSkipped ? `${totalSkipped} skipped ${totalSkipped === 1 ? 'record' : 'records'} in ${sourceLabel}`
                : `${totalUnlinked} ${totalUnlinked === 1 ? 'record' : 'records'} remaining to review`
                : syncing || anySaving ? 'Syncing review counts…'
                : error && loadedScope === scope ? 'Review counts unavailable' : 'Loading review counts…'}
            </div>
            {countsReady && !isSkipped && <>
              <div className="text-sm text-gray-600 font-medium mb-1">
                {totalReviewed} / {totalRecords} reviewed ({progressPercent}%)
              </div>
              <div className="w-48 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-[#1D9E75] h-2 transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Reviewed includes skipped records; counts apply to this queue.</p>
            </>}
          </div>
        </div>
      </div>

      {isSkipped && <p className="px-4 py-3 border-b border-gray-200 text-sm text-gray-600">
        Skipped {config.label} links in {sourceLabel}: reviewed but still unlinked. Select a match and confirm it here.
      </p>}

      {error && loadedScope === scope && <div role="alert" className="bg-[#D85A30]/10 border-b border-[#D85A30] text-[#D85A30] px-4 py-3">
        <p>{error}</p>
        <button type="button" onClick={() => fetchQueue(true)} disabled={anySaving || syncing}
          className="mt-2 px-4 py-2 border rounded text-sm font-medium disabled:opacity-50">Retry queue</button>
      </div>}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-white text-gray-500 text-sm border-b border-gray-200">
              <th className="p-4 font-semibold w-1/4">Book Title</th>
              <th className="p-4 font-semibold w-1/5">Free-text {config.label}</th>
              <th className="p-4 font-semibold w-2/5">Suggestions</th>
              <th className="p-4 font-semibold w-1/5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading || (loadedScope !== scope) ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-500">Loading linking queue...</td></tr>
            ) : items.length === 0 && (syncing || anySaving) ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-500">Syncing linking queue…</td></tr>
            ) : items.length === 0 && error ? (
              <tr><td colSpan={4} className="p-8 text-center">
                Linking queue unavailable.
              </td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-12 text-center">
                  <div className="text-xl font-medium text-gray-800 mb-2">
                    {totalVisible === 0 ? `No ${queueStatus} ${config.label} links in ${sourceLabel}.`
                      : `No records on this page. Reload the ${queueStatus} queue.`}
                  </div>
                  {totalVisible === 0 ? <div className="text-gray-500">Switch source tables to check another queue.</div>
                    : <button type="button" onClick={() => fetchQueue(true)} className="px-4 py-2 border rounded text-sm font-medium">Retry queue</button>}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const freeText = item[config.freeTextField];
                const rawValue = item.raw_genre_field;
                const hasTooltip = config.hasRawField && rawValue && rawValue !== freeText;
                
                // Sort suggestions by match_score descending defensively
                const suggestions = [...(item.suggestions || [])].sort((a, b) => b.match_score - a.match_score);
                
                const isRowLoading = !!busyRecords[item.record_id];
                const isRowLocked = isRowLoading || !!awaitingSync[item.record_id];
                const selectedId = selections[item.record_id];

                return (
                  <tr key={item.record_id} className="hover:bg-gray-50 align-top">
                    <td className="p-4">
                      <div className="font-medium text-gray-900 line-clamp-2" title={item.title || ''}>
                        {item.title || <span className="text-gray-400 italic">— untitled —</span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-800">{freeText}</span>
                        {hasTooltip && (
                          <span 
                            title={`Full value: ${rawValue}`}
                            className="cursor-help text-gray-400 hover:text-gray-600 inline-flex items-center justify-center rounded-full border border-current w-4 h-4 text-[10px]"
                          >
                            i
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      {suggestions.length === 0 ? (
                        <div className="text-sm text-gray-400 italic">No suggestions found</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {suggestions.map((s) => {
                            const id = s[config.idField];
                            const isSelected = selectedId === id;
                            const isHighMatch = s.match_score >= 0.7;
                            
                            let chipClasses = "flex flex-col items-start px-3 py-1.5 rounded border text-sm cursor-pointer transition-all max-w-[220px]";
                            if (isSelected) {
                              chipClasses += " bg-[#1D9E75] border-[#1D9E75] text-white shadow-sm";
                            } else if (isHighMatch) {
                              chipClasses += " bg-emerald-50 border-emerald-300 hover:bg-emerald-100 text-gray-800";
                            } else {
                              chipClasses += " bg-white border-gray-300 hover:bg-gray-100 text-gray-700";
                            }

                            return (
                              <div 
                                key={id} 
                                onClick={() => selectSuggestion(item, id)}
                                className={chipClasses}
                              >
                                <div className="font-semibold line-clamp-1 truncate w-full" title={s[config.nameField]}>
                                  {s[config.nameField]}
                                </div>
                                {s[config.nameSecondaryField] && (
                                  <div className={`text-xs truncate w-full ${isSelected ? 'text-emerald-100' : 'text-gray-500'}`} title={s[config.nameSecondaryField]}>
                                    ({s[config.nameSecondaryField]})
                                  </div>
                                )}
                                <div className={`text-[10px] mt-0.5 font-medium ${isSelected ? 'text-emerald-50' : 'text-gray-400'}`}>
                                  match: {Math.round(s.match_score * 100)}%
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right align-middle">
                      <div className="flex flex-col items-end gap-2">
                        <button
                          disabled={!selectedId || isRowLocked}
                          onClick={() => handleAction(item, 'confirm')}
                          className="w-28 px-3 py-1.5 bg-[#085041] text-white rounded text-sm font-medium hover:bg-opacity-90 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isRowLoading ? (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          ) : "Confirm Link"}
                        </button>
                        {!isSkipped && <button
                          disabled={isRowLocked}
                          onClick={() => handleAction(item, 'skip')}
                          className="w-28 px-3 py-1.5 border border-gray-300 text-gray-700 bg-white rounded text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          Skip
                        </button>}
                        {actionErrors[item.record_id] && <p role="alert" className="text-sm text-red-700">{actionErrors[item.record_id]}</p>}
                        {awaitingSync[item.record_id] && <p className="text-xs text-gray-500">Sync queue before retrying this record.</p>}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <nav aria-label="Review queue pagination" className="p-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-gray-600">
          {countsReady && items.length > 0 ? `Showing ${offset + 1}–${offset + items.length} of ${totalVisible} ${queueStatus} records` : ''}
        </span>
        <div className="flex gap-2">
          <button type="button" disabled={!countsReady || offset === 0}
            onClick={() => changePage(Math.max(0, offset - limit))}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 hover:bg-gray-50 bg-white">Prev</button>
          <button type="button" disabled={!countsReady || offset + limit >= totalVisible}
            onClick={() => changePage(offset + limit)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 hover:bg-gray-50 bg-white">Next</button>
        </div>
      </nav>
    </div>
  );
}
